/**
 * Logto organization role → admin-portal role resolution.
 *
 * Mirrors the backend so the UI shows what the API will actually allow:
 *   - role mapping: services/main-service/internal/auth/logto/mapper.go
 *     (mapLogtoRoleToAiRole)
 *   - org selection: services/main-service/internal/auth/logto/tenant_resolution.go
 *     (ResolveTenant: signed active org → selector → first org)
 *
 * A role is only ever taken from the organization the request is scoped to,
 * never flattened across every organization the person belongs to: an admin
 * of org A who is a viewer of org B must be a viewer while working in B.
 */

export type LogtoAiRole = 'admin' | 'manager' | 'viewer';

export interface LogtoOrgClaims {
  organization_roles?: unknown;
  organizations?: unknown;
  active_organization?: unknown;
}

const ROLE_RANK: Record<LogtoAiRole, number> = {
  viewer: 1,
  manager: 2,
  admin: 3,
};

export const mapLogtoRoleToAiRole = (logtoRole: string): LogtoAiRole => {
  switch (logtoRole) {
    case 'owner':
    case 'admin':
      return 'admin';
    case 'manager':
      return 'manager';
    default:
      return 'viewer';
  }
};

const roleEntries = (claims: LogtoOrgClaims): Array<[string, string]> => {
  if (!Array.isArray(claims.organization_roles)) return [];
  const entries: Array<[string, string]> = [];
  for (const entry of claims.organization_roles) {
    if (typeof entry !== 'string') continue;
    const sep = entry.indexOf(':');
    if (sep <= 0) continue;
    const org = entry.slice(0, sep).trim();
    const role = entry.slice(sep + 1).trim();
    if (org && role) entries.push([org, role]);
  }
  return entries;
};

/** Distinct organizations the token claims, in the backend's order. */
export const logtoOrganizations = (claims: LogtoOrgClaims): string[] => {
  const orgs: string[] = [];
  const add = (org: unknown) => {
    if (typeof org !== 'string') return;
    const trimmed = org.trim();
    if (trimmed && !orgs.includes(trimmed)) orgs.push(trimmed);
  };
  roleEntries(claims).forEach(([org]) => add(org));
  if (Array.isArray(claims.organizations)) claims.organizations.forEach(add);
  return orgs;
};

/**
 * Highest role the token grants inside one organization. A member with no
 * role entry for that organization gets the least-privileged role.
 */
export const logtoRoleForOrganization = (
  claims: LogtoOrgClaims,
  orgId: string
): LogtoAiRole => {
  let best: LogtoAiRole = 'viewer';
  for (const [org, role] of roleEntries(claims)) {
    if (org !== orgId) continue;
    const mapped = mapLogtoRoleToAiRole(role);
    if (ROLE_RANK[mapped] > ROLE_RANK[best]) best = mapped;
  }
  return best;
};

/**
 * Resolves the role for the organization `selector` points at, using only
 * the token. Returns null when that cannot be decided locally — the selector
 * is a canonical tenant UUID (only the backend's tenant map knows which org
 * it belongs to) and the candidate orgs grant different roles. Callers then
 * ask the server which organization was selected.
 */
export const resolveLogtoRole = (
  claims: LogtoOrgClaims | null | undefined,
  selector?: string | null
): LogtoAiRole | null => {
  if (!claims) return 'viewer';
  const active =
    typeof claims.active_organization === 'string'
      ? claims.active_organization.trim()
      : '';
  if (active) return logtoRoleForOrganization(claims, active);

  const orgs = logtoOrganizations(claims);
  if (orgs.length === 0) return 'viewer';

  const selected = (selector ?? '').trim();
  if (!selected) return logtoRoleForOrganization(claims, orgs[0]);
  if (orgs.includes(selected))
    return logtoRoleForOrganization(claims, selected);

  const candidates = new Set(
    orgs.map(org => logtoRoleForOrganization(claims, org))
  );
  return candidates.size === 1 ? [...candidates][0] : null;
};
